import { type Assessment } from "@core/assessments/types";
import { URLS } from "@core/shared/utils";
import { formatDateForDisplay } from "@core/shared/utils/date-formatter";
import { image } from "@core/shared/utils/image";
import { Badge } from "@design-system/components/ui/badge";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import {
  ArrowRight,
  Award,
  Calendar,
  Clock,
  HelpCircle,
  Lock,
  Play,
  Target,
} from "lucide-react";
import { BaseLink } from "../../design-system/components/link";
import { useAssessmentCard } from "../hooks";

type AssessmentCardProps = {
  assessment: Assessment;
};

export function AssessmentCard({ assessment }: AssessmentCardProps) {
  const { isExpired, getStatusInfo } = useAssessmentCard(assessment);
  const statusInfo = getStatusInfo();

  // Map icon names to components
  const getStatusIcon = (iconName: string) => {
    switch (iconName) {
      case "Calendar":
        return Calendar;
      case "Play":
        return Play;
      case "HelpCircle":
        return HelpCircle;
      default:
        return HelpCircle;
    }
  };

  const StatusIcon = getStatusIcon(statusInfo.icon);

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl dark:border-slate-800 dark:bg-slate-950",
        statusInfo.bgGradient,
      )}
    >
      {/* Image Section - Only show if assessment has an image */}
      {assessment.image && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={image(assessment.image, { w: 400, h: 256, q: 80 })}
            alt={assessment.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6">
        <div className="flex items-center gap-3">
          {/* Icon - Only show if no image is provided */}
          {!assessment.image && (
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl">
              <HelpCircle className="h-8 w-8 text-primary-600" />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <BaseLink to={URLS.assessments.view(assessment)}>
              <h3 className="line-clamp-2 text-xl font-black leading-8 text-slate-950 transition-colors group-hover:text-primary dark:text-white">
                {assessment.title}
              </h3>
            </BaseLink>
            {assessment.description && (
              <p className="line-clamp-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {assessment.description}
              </p>
            )}
          </div>
          {/* Status Badge */}
          <div
            className={cn(
              "flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium",
              statusInfo.icon === "Play" && "animate-pulse",
              statusInfo.color,
            )}
          >
            <StatusIcon className={"h-3 w-3"} />
            {statusInfo.label}
          </div>
        </div>

        {/* Enhanced Stats Section */}
        <div className="flex flex-col gap-3">
          {/* Primary Stats */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary-600" />
              <span>
                {trans("assessments.minutes", {
                  count: assessment.duration || 0,
                })}
              </span>
            </Badge>
            {assessment.totalQuestions !== undefined &&
              assessment.totalQuestions > 0 && (
                <Badge variant="secondary" className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-success-600" />
                  <span>
                    {trans("assessments.questions", {
                      count: assessment.totalQuestions,
                    })}
                  </span>
                </Badge>
              )}
            {assessment.passingScore && (
              <Badge variant="secondary" className="flex items-center gap-2">
                <Target className="h-4 w-4 text-orange-600" />
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {assessment.passingScore}% {trans("assessments.toPass")}
                </span>
              </Badge>
            )}
          </div>

          {/* Assessment Type Badge */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="flex items-center gap-2">
              <Award className="h-3 w-3" />
              {assessment.type === "quiz"
                ? trans("assessments.quiz")
                : trans("assessments.exam")}
            </Badge>
            {assessment.grade && (
              <Badge variant="secondary" className="flex items-center gap-2">
                {assessment.grade.name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Section - Dates and Button */}
      <div className="border-t border-slate-100 bg-slate-50/80 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/70">
        {/* Dates Section */}
        {(assessment.startsAt || assessment.endsAt) && (
          <div className="px-6 pt-4">
            <div className="space-y-2 rounded-2xl border border-slate-200 bg-white/80 p-3 text-sm dark:border-slate-700 dark:bg-slate-950/70">
              {assessment.startsAt && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary-600" />
                    <span className="font-medium text-primary-700 dark:text-primary-400">
                      {trans("assessments.startsAt")}
                    </span>
                  </div>
                  {/* Timezone-dependent text: server and browser can differ. */}
                  <span
                    className="font-semibold text-gray-900 dark:text-gray-100"
                    suppressHydrationWarning
                  >
                    {formatDateForDisplay(assessment.startsAt.timestamp)}
                  </span>
                </div>
              )}
              {assessment.endsAt && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary-600" />
                    <span className="font-medium text-primary-700 dark:text-primary-400">
                      {trans("assessments.endsAt")}
                    </span>
                  </div>
                  {/* Timezone-dependent text: server and browser can differ. */}
                  <span
                    className="font-semibold text-gray-900 dark:text-gray-100"
                    suppressHydrationWarning
                  >
                    {formatDateForDisplay(assessment.endsAt.timestamp)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Button Section */}
        <div className="p-6">
          <Link
            to={URLS.assessments.view(assessment)}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400",
              isExpired
                ? "pointer-events-none bg-gray-400 text-white dark:bg-gray-600"
                : "bg-primary text-primary-foreground hover:bg-primary-700",
            )}
          >
            {isExpired ? (
              <>
                <Lock className="h-4 w-4" />
                {trans("assessments.expired")}
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                {trans("assessments.startAssessment")}
              </>
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}
